/**
 * StaffTokenGrantFlowDialog — manager entry point for manually granting
 * tokens to a staff member, reachable from the profile (UserMenu) rather
 * than a specific staff row. Unlike `ManualTokenGrantDialog` (which is keyed
 * to a known `staffId`), this flow lets an authorized manager pick the target
 * staff first, then grant.
 *
 * Reaching the flow needs `location.edit_staff` (caller-gated); the grant
 * itself runs behind the shared reinforced-authorization gate (password or
 * wristband) verified against `location.authorize_staff_tokens`.
 */

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from '@/core';
import { AuthorizeActionDialog, type AuthorizeResult } from '@/shared/components/AuthorizeAction';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/theme/components/ui/select';
import { useStaff } from '../hooks/useStaff';
import { useStaffTokenBalances, useGrantStaffTokensManually } from '../hooks/useStaffTokens';

const AUTHORIZE_PERMISSION = 'location.authorize_staff_tokens';

interface StaffTokenGrantFlowDialogProps {
  open: boolean;
  locationId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function StaffTokenGrantFlowDialog({
  open,
  locationId,
  onOpenChange,
}: StaffTokenGrantFlowDialogProps) {
  const { t } = useTranslation();

  // StaffViewSet already filters to active staff, so this list is the pool of
  // grantable members for the location. Only fetch while the dialog is open.
  const { data: staff = [], isLoading: staffLoading } = useStaff(
    open && locationId ? { locationId } : undefined,
  );
  const { data: balances = [] } = useStaffTokenBalances(open ? locationId : null);

  const [staffId, setStaffId] = useState('');
  const [amount, setAmount] = useState('');

  const grantMutation = useGrantStaffTokensManually(staffId, locationId);

  const currentBalance = useMemo(
    () => balances.find((b) => b.staffId === staffId) ?? null,
    [balances, staffId],
  );

  // Staff picker + amount live as children; reset them per opening.
  useEffect(() => {
    if (open) {
      setStaffId('');
      setAmount('');
    }
  }, [open]);

  // Tokens are whole units — the backend endpoint is an IntegerField.
  const parsed = Math.trunc(parseFloat(amount));
  const isValid = Boolean(staffId) && amount.trim() !== '' && !isNaN(parsed) && parsed >= 1;

  const handleConfirm = async (auth: AuthorizeResult) => {
    await grantMutation.mutateAsync({
      amount: parsed,
      reason: auth.reason ?? '',
      approverPassword: auth.approverPassword,
      approverNfcUid: auth.approverNfcUid,
    });
    onOpenChange(false);
  };

  return (
    <AuthorizeActionDialog
      open={open}
      onOpenChange={onOpenChange}
      requires="authorize"
      permissions={[AUTHORIZE_PERMISSION]}
      variant="default"
      confirmDisabled={!isValid}
      title={t('staff.tokens.grantDialog.titleGrant', 'Grant tokens')}
      description={t(
        'staff.tokens.grantDialog.flowDescription',
        'Manually grant tokens to a staff member at this location.',
      )}
      confirmLabel={t('staff.tokens.grantDialog.confirmGrant', 'Grant tokens')}
      reasonPlaceholder={t('staff.tokens.grantDialog.notesPlaceholderGrant', 'e.g. Bonus for covering a shift')}
      onConfirm={handleConfirm}
    >
      <div className="space-y-2">
        <Label htmlFor="grant-staff">
          {t('staff.tokens.grantDialog.selectStaffLabel', 'Staff member')}
        </Label>
        <Select value={staffId} onValueChange={setStaffId} disabled={staffLoading}>
          <SelectTrigger id="grant-staff">
            <SelectValue
              placeholder={
                staffLoading
                  ? t('common.loading', 'Loading…')
                  : t('staff.tokens.grantDialog.selectStaffPlaceholder', 'Select a staff member')
              }
            />
          </SelectTrigger>
          <SelectContent>
            {staff.length === 0 && !staffLoading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('staff.tokens.grantDialog.noStaff', 'No staff members available.')}
              </div>
            ) : (
              staff.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.person?.name || t('common.user', 'User')}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {currentBalance && (
        <div className="rounded-md border bg-muted/40 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {t('staff.tokens.grantDialog.currentBalance', 'Current balance')}:{' '}
            <span className="font-semibold tabular-nums">{currentBalance.availableTokens}</span>
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="grant-amount">
          {t('staff.tokens.grantDialog.amountLabel', 'Amount (min. 1)')}
        </Label>
        <Input
          id="grant-amount"
          type="number"
          min="1"
          step="1"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-40"
        />
      </div>
    </AuthorizeActionDialog>
  );
}
